import React, { useState } from 'react'
import CloseIcon from '@material-ui/icons/Close'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { Box, ButtonBase, Grid, Typography } from '@material-ui/core'
import useFormikHelper from './useFormikHelper'
import { useRouter } from 'next/router'
import InfoCircleIcon from '@src/icons/InfoCircleIcon'
import { truncateLongString } from './utils'
import {
  SingleTubeFormValues,
  useProductFromQueryParam,
} from './ProductsConfiguration'
import DeleteModal from '../../../../components/DeleteModal'
import { isNil } from 'lodash'
import useTubeValidation from './useTubeValidation'
import { TubeProperties } from '../../utils/StandardMTOEnum'
import { BiTubeProduct } from '../BiTube/enum'

const useStyles = makeStyles((theme: Theme) => ({
  listContainer: {
    paddingRight: theme.spacing(2),
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#888',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#555',
    },
  },
  scrollable: {
    maxHeight: '280px',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  nonScrollable: {
    overflow: 'hidden',
  },
  itemContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    minHeight: '40px',
  },
  closeIconStyle: {
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: 'bold',
    stroke: 'currentColor',
    strokeWidth: 2,
    marginRight: theme.spacing(1),
  },
  linkStyle: {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: '14px',
    lineHeight: '20px',
  },
  infoIconStyle: {
    fontSize: 'inherit',
    cursor: 'pointer',
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.error.main,
  },
  oligoNameWithIcon: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}))

const MyOligosList = () => {
  const formik = useFormikHelper()
  const classes = useStyles()
  const router = useRouter()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [idToBeDeleted, setIdToBeDeleted] = useState<number | null>(null)
  const product = useProductFromQueryParam()
  const { validationAPI } = useTubeValidation()

  const isMirna = product === BiTubeProduct.mirna

  const nameType = isMirna ? TubeProperties.AntiSenseName : TubeProperties.Name

  const isduplex =
    product === BiTubeProduct.Duplex ||
    product === BiTubeProduct.FastDuplex ||
    product === BiTubeProduct.mirna

  const handleListItemClick = async (index: number) => {
    formik.touchRequiredFields()
    formik.setFieldValue('activeOligoIndex', index, false)
    // This API Respond with ListErrors
    const response = await validationAPI({
      ...formik.validationFields,
    })
    if (response.length > 0) {
      formik.setOligoFieldValue('displayPageError', true, false)
    }
    router.replace(
      {
        pathname: router.pathname,
        query: router.query,
      },
      undefined,
      { shallow: true }
    )
  }

  const handleDeleteItem = (index: number) => {
    const updatedOligoList = formik.allValues.oligoList.filter(
      (_, i) => i !== index
    )
    const newActiveIndex = Math.max(
      0,
      Math.min(index, updatedOligoList.length - 1)
    )
    formik.removeItemFromOligoList(index)
    formik.setFieldValue('activeOligoIndex', newActiveIndex, false)
    router.replace(
      {
        pathname: router.pathname,
        query: router.query,
      },
      undefined,
      { shallow: true }
    )
    setDialogOpen(false)
  }

  const handleCloseIconClick = (id: number) => {
    if (isNil(id)) return
    setIdToBeDeleted(id)
    formik.setFieldValue('activeOligoIndex', id, false)
    setDialogOpen(true)
  }

  const needsScroll = formik.allValues.oligoList.length > 7

  return (
    <>
      <Box
        className={`${classes.listContainer} ${
          needsScroll ? classes.scrollable : classes.nonScrollable
        }`}
      >
        {formik.allValues.oligoList.map(
          (oligo: SingleTubeFormValues, index: number) => (
            <Grid container spacing={4} key={`oligo-${index}`}>
              <Grid item className={classes.itemContainer}>
                <Box className={classes.oligoNameWithIcon}>
                  {oligo?.displayPageError && (
                    <InfoCircleIcon className={classes.infoIconStyle} />
                  )}
                  <ButtonBase onClick={() => handleListItemClick(index)}>
                    <Typography
                      className={classes.linkStyle}
                      title={oligo[nameType] || ''}
                    >
                      {(oligo?.[nameType]?.trim() &&
                        truncateLongString(oligo[nameType] || '', 14)) ||
                        (isduplex
                          ? `Duplex ${index + 1}`
                          : `Oligo ${index + 1}`)}
                    </Typography>
                  </ButtonBase>
                </Box>
                <CloseIcon
                  onClick={() => handleCloseIconClick(index)}
                  className={classes.closeIconStyle}
                />
              </Grid>
            </Grid>
          )
        )}
      </Box>
      <DeleteModal
        dialogOpen={dialogOpen}
        handleClose={() => setDialogOpen(false)}
        handleDelete={() =>
          !isNil(idToBeDeleted) && handleDeleteItem(idToBeDeleted)
        }
        deleteType="single"
      />
    </>
  )
}

export default MyOligosList
